import React, { useState, useCallback } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { Modal } from '../common/Modal';

// ============================================================================
// Types
// ============================================================================

export interface Subscription {
  id: number;
  kind: string;
  billingSource: string | null;
  startsAt: string;
  endsAt: string;
  active: boolean;
}

export interface DiaryDay {
  id: number;
  date: string;
  activitiesCount: number;
  mood: number | null;
  createdAt: string;
}

export interface Activity {
  id: number;
  fieldName: string | null;
  active: boolean;
  date: string;
  triggerType: 'manual' | 'auto' | null;
}

export interface UserData {
  id: number;
  email: string;
  betaTester: boolean;
  createdAt: string;
  diaryDaysCount: number;
  activitiesCount: number;
  activeActivitiesCount: number;
  accountAgeDays: number;
}

export interface AdminUserDetailPageProps {
  user: UserData;
  activeSubscription: Subscription | null;
  subscriptions: Subscription[];
  diaryDays: DiaryDay[];
  recentActivities: Activity[];

  // URLs/paths
  usersPath: string;
  userPath: string;
  addSubscriptionPath: string;
  deleteSubscriptionPath: (id: number) => string;
  toggleBetaTesterPath: string;

  // CSRF token
  csrfToken: string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    // User info
    userInfoTitle: string;
    emailLabel: string;
    userIdLabel: string;
    registeredLabel: string;
    betaTesterLabel: string;
    yesLabel: string;
    noLabel: string;
    removeLabel: string;
    enableLabel: string;
    // Activity stats
    activityStatsTitle: string;
    totalDiaryDaysLabel: string;
    totalActivitiesLabel: string;
    activeActivitiesLabel: string;
    accountAgeLabel: string;
    daysLabel: string;
    // Subscription
    subscriptionStatusTitle: string;
    activeLabel: string;
    freeLabel: string;
    addLabel: string;
    typeLabel: string;
    billingSourceLabel: string;
    manualLabel: string;
    startedLabel: string;
    expiresLabel: string;
    noActiveSubscription: string;
    // Subscription modal
    addSubscriptionTitle: string;
    selectDurationLabel: string;
    oneMonth: string;
    twoMonths: string;
    threeMonths: string;
    oneYear: string;
    lifetime: string;
    // Subscription history
    subscriptionHistoryTitle: string;
    periodLabel: string;
    expiredLabel: string;
    pendingLabel: string;
    deleteLabel: string;
    // Diary days
    recentDiaryDaysTitle: string;
    dateLabel: string;
    activitiesLabel: string;
    moodLabel: string;
    createdLabel: string;
    noDiaryDays: string;
    agoLabel: string;
    // Activities
    recentActivitiesTitle: string;
    fieldLabel: string;
    statusLabel: string;
    triggeredLabel: string;
    inactiveLabel: string;
    autoLabel: string;
    noActivities: string;
  };
}

// ============================================================================
// Helper Components
// ============================================================================

function InfoBox({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="p-3 bg-light rounded">
      <div className="text-muted small mb-1">{label}</div>
      <div className={`fw-bold ${valueClass || ''}`}>{value}</div>
    </div>
  );
}

function StatBox({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="p-3 bg-light rounded text-center">
      <div className="text-muted small mb-1">{label}</div>
      <div className={`h3 mb-0 text-${color}`}>{value}</div>
    </div>
  );
}

function formatDate(dateString: string, format: 'short' | 'long' = 'short'): string {
  const date = new Date(dateString);
  if (format === 'long') {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    }
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  }
  const months = Math.floor(diffDays / 30);
  return `${months} month${months !== 1 ? 's' : ''}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminUserDetailPage({
  user,
  activeSubscription,
  subscriptions,
  diaryDays,
  recentActivities,
  usersPath,
  userPath,
  addSubscriptionPath,
  deleteSubscriptionPath,
  toggleBetaTesterPath,
  csrfToken,
  labels,
}: AdminUserDetailPageProps) {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const handleToggleBetaTester = useCallback(async () => {
    setIsToggling(true);
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = toggleBetaTesterPath;

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'PATCH';
      form.appendChild(methodInput);

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      const betaTesterInput = document.createElement('input');
      betaTesterInput.type = 'hidden';
      betaTesterInput.name = 'user[beta_tester]';
      betaTesterInput.value = (!user.betaTester).toString();
      form.appendChild(betaTesterInput);

      document.body.appendChild(form);
      form.submit();
    } catch {
      setIsToggling(false);
    }
  }, [toggleBetaTesterPath, csrfToken, user.betaTester]);

  const handleAddSubscription = useCallback(
    (duration: string) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${addSubscriptionPath}?duration=${duration}`;

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    },
    [addSubscriptionPath, csrfToken]
  );

  const handleDeleteSubscription = useCallback(
    (id: number) => {
      if (!confirm('Are you sure you want to delete this subscription?')) return;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = deleteSubscriptionPath(id);

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'DELETE';
      form.appendChild(methodInput);

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    },
    [deleteSubscriptionPath, csrfToken]
  );

  const subscriptionDurations = [
    { key: '1_month', label: labels.oneMonth, icon: 'calendar', variant: 'outline-primary' },
    { key: '2_months', label: labels.twoMonths, icon: 'calendar', variant: 'outline-primary' },
    { key: '3_months', label: labels.threeMonths, icon: 'calendar', variant: 'outline-primary' },
    { key: '1_year', label: labels.oneYear, icon: 'calendar-check', variant: 'outline-primary' },
    { key: 'lifetime', label: labels.lifetime, icon: 'infinity', variant: 'success' },
  ];

  return (
    <div>
      <AdminPageHeader
        title={`${labels.title} #${user.id}`}
        subtitle={user.email}
        icon="person-circle"
        backLink={{ href: usersPath, label: labels.backLabel }}
      />

      {/* User Info and Activity Stats */}
      <div className="row g-4 mb-4">
        {/* User Info Card */}
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2" />
                {labels.userInfoTitle}
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <InfoBox label={labels.emailLabel} value={user.email} />
                </div>
                <div className="col-md-6">
                  <InfoBox label={labels.userIdLabel} value={`#${user.id}`} />
                </div>
                <div className="col-md-6">
                  <InfoBox
                    label={labels.registeredLabel}
                    value={formatDate(user.createdAt, 'long')}
                  />
                </div>
                <div className="col-12">
                  <div className="p-3 bg-light rounded d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small mb-1">{labels.betaTesterLabel}</div>
                      <div className="fw-bold">
                        {user.betaTester ? (
                          <span className="badge bg-info">{labels.yesLabel}</span>
                        ) : (
                          <span className="badge bg-secondary">{labels.noLabel}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        user.betaTester ? 'btn-outline-secondary' : 'btn-outline-info'
                      }`}
                      onClick={handleToggleBetaTester}
                      disabled={isToggling}
                    >
                      {user.betaTester ? (
                        <>
                          <i className="bi bi-x-circle me-1" />
                          {labels.removeLabel}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1" />
                          {labels.enableLabel}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Stats Card */}
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2" />
                {labels.activityStatsTitle}
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <StatBox
                    label={labels.totalDiaryDaysLabel}
                    value={user.diaryDaysCount}
                    color="primary"
                  />
                </div>
                <div className="col-md-6">
                  <StatBox
                    label={labels.totalActivitiesLabel}
                    value={user.activitiesCount}
                    color="success"
                  />
                </div>
                <div className="col-md-6">
                  <StatBox
                    label={labels.activeActivitiesLabel}
                    value={user.activeActivitiesCount}
                    color="info"
                  />
                </div>
                <div className="col-md-6">
                  <StatBox
                    label={labels.accountAgeLabel}
                    value={`${user.accountAgeDays} ${labels.daysLabel}`}
                    color="warning"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Section */}
      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-star me-2" />
                {labels.subscriptionStatusTitle}
              </h5>
              <div className="d-flex align-items-center gap-2">
                {activeSubscription ? (
                  <span className="badge bg-success">{labels.activeLabel}</span>
                ) : (
                  <span className="badge bg-secondary">{labels.freeLabel}</span>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  <i className="bi bi-plus-lg" /> {labels.addLabel}
                </button>
              </div>
            </div>
            <div className="card-body">
              {activeSubscription ? (
                <div className="row g-3">
                  <div className="col-md-6">
                    <InfoBox
                      label={labels.typeLabel}
                      value={
                        <span className="text-capitalize">{activeSubscription.kind}</span>
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <InfoBox
                      label={labels.billingSourceLabel}
                      value={
                        <span className="text-capitalize">
                          {activeSubscription.billingSource || labels.manualLabel}
                        </span>
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <InfoBox
                      label={labels.startedLabel}
                      value={formatDate(activeSubscription.startsAt)}
                    />
                  </div>
                  <div className="col-md-6">
                    <InfoBox
                      label={labels.expiresLabel}
                      value={formatDate(activeSubscription.endsAt)}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-star" style={{ fontSize: '2rem' }} />
                  <p className="mt-2 mb-0">{labels.noActiveSubscription}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Subscription Modal */}
      <Modal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        title={labels.addSubscriptionTitle}
        icon="plus-circle"
      >
        <p className="text-muted mb-3">
          {labels.selectDurationLabel} <strong>{user.email}</strong>
        </p>
        <div className="d-grid gap-2">
          {subscriptionDurations.map((duration) => (
            <button
              key={duration.key}
              type="button"
              className={`btn ${duration.variant}`}
              onClick={() => handleAddSubscription(duration.key)}
            >
              <i className={`bi bi-${duration.icon} me-2`} />
              {duration.label}
            </button>
          ))}
        </div>
      </Modal>

      {/* Subscription History */}
      {subscriptions.length > 0 && (
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <h5 className="mb-0">
                  <i className="bi bi-clock-history me-2" />
                  {labels.subscriptionHistoryTitle}
                </h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{labels.typeLabel}</th>
                        <th>{labels.statusLabel}</th>
                        <th>{labels.billingSourceLabel}</th>
                        <th>{labels.periodLabel}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((subscription) => {
                        const isExpired = new Date(subscription.endsAt) < new Date();
                        let statusBadge;
                        if (subscription.active) {
                          statusBadge = (
                            <span className="badge bg-success">{labels.activeLabel}</span>
                          );
                        } else if (isExpired) {
                          statusBadge = (
                            <span className="badge bg-secondary">{labels.expiredLabel}</span>
                          );
                        } else {
                          statusBadge = (
                            <span className="badge bg-warning">{labels.pendingLabel}</span>
                          );
                        }

                        return (
                          <tr key={subscription.id}>
                            <td className="text-capitalize fw-semibold">{subscription.kind}</td>
                            <td>{statusBadge}</td>
                            <td className="text-capitalize">
                              {subscription.billingSource || labels.manualLabel}
                            </td>
                            <td>
                              {formatDate(subscription.startsAt)} -{' '}
                              {formatDate(subscription.endsAt)}
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteSubscription(subscription.id)}
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Diary Days */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-calendar3 me-2" />
                {labels.recentDiaryDaysTitle}
              </h5>
            </div>
            <div className="card-body">
              {diaryDays.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{labels.dateLabel}</th>
                        <th>{labels.activitiesLabel}</th>
                        <th>{labels.moodLabel}</th>
                        <th>{labels.createdLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diaryDays.map((day) => (
                        <tr key={day.id}>
                          <td className="fw-semibold">{formatDate(day.date, 'long')}</td>
                          <td>
                            <span className="badge bg-primary">{day.activitiesCount}</span>
                          </td>
                          <td>
                            {day.mood !== null ? (
                              <span
                                className={`badge bg-${day.mood >= 0 ? 'success' : 'warning'}`}
                              >
                                {day.mood}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {timeAgo(day.createdAt)} {labels.agoLabel}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-calendar3" style={{ fontSize: '2rem' }} />
                  <p className="mt-2 mb-0">{labels.noDiaryDays}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="bi bi-activity me-2" />
                {labels.recentActivitiesTitle}
              </h5>
            </div>
            <div className="card-body">
              {recentActivities.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{labels.fieldLabel}</th>
                        <th>{labels.statusLabel}</th>
                        <th>{labels.dateLabel}</th>
                        <th>{labels.triggeredLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivities.map((activity) => (
                        <tr key={activity.id}>
                          <td>{activity.fieldName || 'Unknown'}</td>
                          <td>
                            {activity.active ? (
                              <span className="badge bg-success">{labels.activeLabel}</span>
                            ) : (
                              <span className="badge bg-secondary">{labels.inactiveLabel}</span>
                            )}
                          </td>
                          <td>{formatDate(activity.date)}</td>
                          <td>
                            {activity.triggerType === 'manual' ? (
                              <span className="badge bg-primary">Manual</span>
                            ) : activity.triggerType === 'auto' ? (
                              <span className="badge bg-info">{labels.autoLabel}</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-activity" style={{ fontSize: '2rem' }} />
                  <p className="mt-2 mb-0">{labels.noActivities}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUserDetailPage;
